import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { LoadingManager } from "./components/loading/loading-manager";
import sentry from "./sentry";
import { ServerApiAccess } from "./server-api-access";
import mobileLoginWorkflow from "./views/mobile-login-workflow";

const PRODUCT_BASE = import.meta.env.VITE_PRODUCT_BASE || "/";
const PRODUCT_URL =
  import.meta.env.VITE_PRODUCT_URL || "http://localhost:8080/";
export const PRODUCT_STAGE = import.meta.env.VITE_PRODUCT_STAGE || "DEV";

const AUTH_SERVER_URL =
  import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3000/";

export type PROVIDER =
  | "direct"
  | "direct-dev"
  | "direct-staging"
  | "direct-mobile";
// OIDCログイン関係
const LOGIN_PATH = (provider: PROVIDER) => {
  switch (provider) {
    case "direct":
      return `${PRODUCT_BASE}oauth2/login`;
    case "direct-dev":
      return `${PRODUCT_BASE}oauth2/login-dev`;
    case "direct-staging":
      return `${PRODUCT_BASE}oauth2/login-stg`;
    case "direct-mobile":
      return `${PRODUCT_BASE}oauth2/login-mbl`;
    default:
      return `${PRODUCT_BASE}oauth2/login`;
  }
};
const OIDC_LOGIN_URL = (provider: PROVIDER) =>
  new URL(LOGIN_PATH(provider), AUTH_SERVER_URL).toString();
const OIDC_LOGOUT_URL = new URL(
  `${PRODUCT_BASE}oauth2/logout`,
  AUTH_SERVER_URL,
).toString();
const OIDC_SUCCESS_REDIRECT = PRODUCT_URL;

export type ServerUserInfo = {
  id: string; //!< direct id
  name: string; //!< ユーザー名
  profileImageUrl: string; //!< プロフィール画像のURL
  domains: any; //!< domains API の結果
  token: string; //!< access token
  idToken: string; //!< id token
  // refreshToken: string,       //!< refresh token
  exp: number; //!< token 有効期限
  hasMessagesWriteScope?: boolean; //!< messages.write スコープを持っているか
};

export default class AppAuth {
  /** 掲示板の認証サーバー上のユーザー情報を取得します */
  public static async getServerUserInfo(): Promise<
    ServerUserInfo | number | undefined
  > {
    const access = new ServerApiAccess();
    return await access.getUserData();
  }

  /** direct-authサーバから OidcLoginUrl を取得するためのURLを作成する */
  public static getOidcLoginUrl(provider: PROVIDER, option?: string) {
    // query部分の構築
    const params = new URLSearchParams();
    if (option) {
      params.append("opt", option);
    }
    const id = uuidv4();
    params.append("trace-id", id);

    const urlBase = OIDC_LOGIN_URL(provider);
    const url = new URL(urlBase);
    url.search = params.toString();
    return url.toString();
  }

  /**
   * サインイン処理を行います
   *
   * direct-js の操作には direct の OIDC Access Token を用います
   * この access token の有効期限は 60 分のため、定期的に refresh token で更新します
   * しかし、この refresh token は OIDC の scope に offline_access を、
   * さらに prompt に consent を指定する必要があります。
   *
   * サーバーセッションが未確立（401）の場合は direct-auth サーバの
   * OIDC ログイン URL へ遷移し、確立済みであればアプリのトップへ遷移します。
   *
   * @param provider
   * @param option モバイルの場合 OS-Type を渡す
   * @returns
   */
  public static async signIn(
    provider: PROVIDER,
    option?: "ios" | "and",
  ): Promise<any> {
    // direct-js 側のセッション情報を取得
    const serverUserInfo = await AppAuth.getServerUserInfo();
    console.log("★ServerUserInfo:%O", serverUserInfo);
    if (typeof serverUserInfo === "number") {
      if (serverUserInfo === 401) {
        // direct-js 用のサーバーセッションを行ってから再度やり直し
        const url = AppAuth.getOidcLoginUrl(provider, option);
        window.location.href = url;
        return;
      } else {
        // その他のエラー
        sentry.sendSentryError(serverUserInfo);
        return;
      }
    } else if (serverUserInfo === undefined) {
      // その他のエラー
      sentry.sendSentryError("unknown error");
      // direct-js 用のサーバーセッションを行ってから再度やり直し
      const url = AppAuth.getOidcLoginUrl(provider, option);
      window.location.href = url;
      return;
    }

    // サーバーセッションが確立済みなので Success 先に移動する
    window.location.href = OIDC_SUCCESS_REDIRECT;
    return;
  }

  /**
   * モバイル向けログイン処理
   * @return string       directNative側に渡す ログインフロー遷移先URL
   * @return ""           ログインフロー遷移先URLの取得失敗
   * @return undefined    ログイン済み。遷移は location.href で行っているためこれ以上処理は不要
   */
  public static async signInMobile(
    provider: PROVIDER,
    option?: "ios" | "and",
  ): Promise<"" | undefined | string> {
    // direct-js 側のセッション情報を取得
    const serverUserInfo = await AppAuth.getServerUserInfo();
    console.log("★ServerUserInfo(Mobile):%O", serverUserInfo);
    if (typeof serverUserInfo === "number") {
      if (serverUserInfo === 401) {
        // direct-js 用のサーバーセッションを行ってから再度やり直し
        const url = AppAuth.getOidcLoginUrl(provider, option);

        // web版のように window.location.href で遷移するのではなく、xhrで取得する
        // 200OK の Location ヘッダーに次の遷移先が記入される
        const result = await axios.get(url, { withCredentials: true });
        if (result.status === 200) {
          const location = result.headers.location;
          return location;
        } else {
          sentry.sendSentryError(serverUserInfo);
          return "";
        }
      } else {
        // その他のエラー
        sentry.sendSentryError(serverUserInfo);
        return "";
      }
    } else if (serverUserInfo === undefined) {
      // その他のエラー
      sentry.sendSentryError("unknown error");
      // direct-js 用のサーバーセッションを行ってから再度やり直し
      const url = AppAuth.getOidcLoginUrl(provider, option);
      const result = await axios.get(url);
      if (result.status === 200) {
        const location = result.headers.location;
        return location;
      } else {
        sentry.sendSentryError(serverUserInfo);
        return "";
      }
    }

    // サーバーセッションが確立済みなので Success 先に移動する
    window.location.href = OIDC_SUCCESS_REDIRECT;
    return undefined; // 遷移済みなのでこれ以上処理不要
  }

  public static async redirectOidcLoginUrl(
    provider: PROVIDER,
    option?: "ios" | "and",
  ): Promise<void> {
    if (!option) {
      const url = AppAuth.getOidcLoginUrl(provider, option);
      window.location.href = url;
      return;
    } else {
      enum LoadingMngKey {
        LOGIN_MOBILE_REDIRECT = "loginMobile",
      }
      try {
        await mobileLoginWorkflow(provider, option, (msg: string) => {
          LoadingManager.start(LoadingMngKey.LOGIN_MOBILE_REDIRECT, msg);
        });
      } catch (error) {
        sentry.sendSentryError(error);
      } finally {
        LoadingManager.stop(LoadingMngKey.LOGIN_MOBILE_REDIRECT);
      }
    }
  }

  public static async signOut(): Promise<string | undefined> {
    try {
      console.log("SignOut");
      const response = await axios.get(OIDC_LOGOUT_URL, {
        withCredentials: true,
      });
      if (response && response.status === 200) {
        return response.data;
      } else {
        return undefined;
      }
    } catch (err) {
      console.log("SignOut Error: ", err);
      return undefined;
    }
  }

  // モバイル向けサインアウト処理(/logoutには遷移しない)
  // /logoutに遷移すると device 情報が失われるため
  public static async signOutMobile(): Promise<void> {
    console.log("SignOut(Mobile)");
    try {
      // 本来は下記の仕組みにして、location遷移をなくしたいが
      // axiosだと session cookie が削除されないため遷移している
      // /logout を get したときに session cookie を削除する API Server
      // にしたら 復活できるはず
      // await axios.get( OIDC_LOGOUT_URL );
      const response = await axios.get(OIDC_LOGOUT_URL, {
        withCredentials: true,
      });
      if (response && response.status === 200 && response.data) {
        window.location.href = response.data;
      }
    } catch (err) {
      console.log(err);
    }
  }
}
